import React, {
  ButtonHTMLAttributes,
  isValidElement,
  memo,
  MouseEvent,
  ReactNode,
  useMemo,
} from 'react';
import {
  ThreeDotsLoader,
  ThreeDotsProps,
} from '../../assets/icons/ThreeDotsLoader';
import { ButtonThemes } from '../../libraries/ButtonTheme';
import { TooltipPosition } from '../../libraries/Tooltips';
import {
  getMergedClassNames,
  useMergedClassNames,
} from '../../utils/HTMLutils';
import './Button.scss';
const rootClassName = 'component-button';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  'tooltip-position'?: TooltipPosition;
  'tooltip-text'?: string;
  theme?: ButtonThemes;
  w100?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  mobileBottomPage?: boolean;
  loading?: boolean;
  loader?: ReactNode | ThreeDotsProps;
}

function Button({
  className,
  theme = ButtonThemes.Primary,
  w100,
  leftIcon,
  rightIcon,
  mobileBottomPage,
  loader,
  loading,
  children,
  ...props
}: ButtonProps) {
  const handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
    if (!props.disabled && !loading) {
      props.onClick?.(evt);
    }
  };

  const loaderElement = useMemo(
    () => (
      <div className={`${rootClassName}-loader`}>
        {loader && isValidElement(loader) ? (
          loader
        ) : (
          <ThreeDotsLoader
            size={1}
            theme={
              theme === ButtonThemes.Primary && !props.disabled
                ? 'secondary'
                : 'primary'
            }
            {...((loader as ThreeDotsProps) ?? {})}
          />
        )}
      </div>
    ),
    [loader, props.disabled, theme]
  );

  return (
    <button
      {...props}
      className={useMergedClassNames([
        'nunito',
        rootClassName,
        className,
        `${rootClassName}-${theme}`,
        w100 ? `${rootClassName}-w100` : '',
        mobileBottomPage ? `${rootClassName}-bottom-mobile` : '',
      ])}
      onClick={handleClick}
    >
      {leftIcon && (
        <div
          className={getMergedClassNames([
            `${rootClassName}-icon`,
            `${rootClassName}-left-icon`,
            loading ? `${rootClassName}-hide` : '',
          ])}
        >
          {leftIcon}
        </div>
      )}
      <div
        className={`${rootClassName}-label nunito ${
          loading ? `${rootClassName}-hide` : ''
        }`}
      >
        {children}
      </div>
      {rightIcon && (
        <div
          className={getMergedClassNames([
            `${rootClassName}-icon`,
            `${rootClassName}-right-icon`,
            loading ? `${rootClassName}-hide` : '',
          ])}
        >
          {rightIcon}
        </div>
      )}
      {loading ? loaderElement : undefined}
    </button>
  );
}

export default memo(Button);
